import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
  ModalOverlay,
  RadioBox,
  RadioGroup,
} from '@dev-spendesk/grapes';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import { type I18nKey, useTranslation } from 'common/hooks/useTranslation';

type Steps = 'confirmIdentity' | 'shareMethod';
export type RecoveryShareMethod = 'manual' | 'email';

const RECOVERY_SHARE_METHODS: RecoveryShareMethod[] = ['manual', 'email'];

const RECOVERY_SHARE_METHODS_CONFIG: Record<
  RecoveryShareMethod,
  {
    label: I18nKey;
    description: I18nKey;
    iconName: 'eye' | 'envelope';
  }
> = {
  manual: {
    label:
      'members.editModal.security.confirmRequesterIdentityModal.shareMethodModal.manual.title',
    description:
      'members.editModal.security.confirmRequesterIdentityModal.shareMethodModal.manual.description',
    iconName: 'eye',
  },
  email: {
    label:
      'members.editModal.security.confirmRequesterIdentityModal.shareMethodModal.email.title',
    description:
      'members.editModal.security.confirmRequesterIdentityModal.shareMethodModal.email.description',
    iconName: 'envelope',
  },
};

type Props = {
  onCancel: () => void;
  onConfirm: (shareMethod: RecoveryShareMethod) => void;
};

export const ConfirmRequesterIdentityModal = ({
  onCancel,
  onConfirm,
}: Props) => {
  const [currentStep, setCurrentStep] = useState<Steps>('confirmIdentity');

  return (
    <ModalOverlay isOpen>
      {currentStep === 'confirmIdentity' && (
        <ConfirmIdentityModal
          onCancel={onCancel}
          onConfirm={() => {
            setCurrentStep('shareMethod');
          }}
        />
      )}
      {currentStep === 'shareMethod' && (
        <SelectRecoveryMethodModal
          onCancel={onCancel}
          onConfirm={(recoveryMethod) => {
            onConfirm(recoveryMethod);
          }}
        />
      )}
    </ModalOverlay>
  );
};

const ConfirmIdentityModal = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('global');

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        iconName="triangle-warning"
        iconVariant="warning"
        title={t(
          'members.editModal.security.confirmRequesterIdentityModal.title',
        )}
      />
      <ModalBody>
        <Trans
          className="text-content-secondary-bg-primary body-m"
          i18nKey="members.editModal.security.confirmRequesterIdentityModal.description"
        />
      </ModalBody>
      <ModalFooter>
        <Button
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          onClick={onCancel}
          key="cancel"
        />
        <Button
          key="submit"
          text={t(
            'members.editModal.security.confirmRequesterIdentityModal.confirmButton',
          )}
          variant="primaryWarning"
          onClick={onConfirm}
        />
      </ModalFooter>
    </ModalContent>
  );
};

const SelectRecoveryMethodModal = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (shareMethod: RecoveryShareMethod) => void;
}) => {
  const { t } = useTranslation('global');

  const [selectedRecoveryMethod, setSelectedRecoveryMethod] =
    useState<RecoveryShareMethod | null>(null);

  const options = RECOVERY_SHARE_METHODS.map((method) => {
    const config = RECOVERY_SHARE_METHODS_CONFIG[method];

    return (
      <RadioBox
        description={
          <p className="text-content-secondary-bg-primary body-m">
            <Trans
              i18nKey={config.description}
              components={{
                bold: <span className="title-m" />,
              }}
            />
          </p>
        }
        iconName={config.iconName}
        key={method}
        label={t(config.label)}
        value={method}
      />
    );
  });

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        iconName="lock-open"
        iconVariant="neutral"
        title={t(
          'members.editModal.security.confirmRequesterIdentityModal.shareMethodModal.title',
        )}
      />
      <ModalBody>
        <RadioGroup
          name="recoveryMethod"
          value={selectedRecoveryMethod}
          direction="column"
          className="mt-16 gap-8 text-start"
          onChange={(event) =>
            setSelectedRecoveryMethod(event.target.value as RecoveryShareMethod)
          }
        >
          {options}
        </RadioGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          onClick={onCancel}
          key="cancel"
        />
        <Button
          key="submit"
          text={t('misc.continue')}
          variant="primaryWarning"
          isDisabled={!selectedRecoveryMethod}
          onClick={() => {
            if (selectedRecoveryMethod) {
              onConfirm(selectedRecoveryMethod);
            }
          }}
        />
      </ModalFooter>
    </ModalContent>
  );
};
